import './SCSS/bundle.scss';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { builder } from '@builder.io/react';
import reportWebVitals from './reportWebVitals';
import { getStore, initializeApplicationState, myBrowserHistory } from './ApplicationState';
import { initializeMixpanel } from './mixpanel';
import initializeErrorTracker from './error-tracker';
import { initializeApplicationRoot } from './identity';
import { fixedEncodeURIComponent, parseQuery } from './GlobalFunctions';
import Root from './Root';
import { SingleMessagePage } from './Pages/SingleMessagePage';
import logoImage from './images/logo-pkp-horizontal-colour.png';
import { getPlatformMode } from './BackendInterface';
import './RegisterComponents/builder-register';

main();

function initializeBuilder() {
  const hostname = window.location.hostname;
  const href = window.location.href;

  // Check if we're in Builder.io preview context
  const isBuilderPreview =
    hostname.includes('builder.io') ||
    hostname.includes('builder.codes') ||
    href.includes('builder.preview') ||
    window.parent !== window;

  // Determine the actual target domain
  let targetDomain = hostname;
  if (isBuilderPreview) {
    if (href.includes('test__peekapak__com') || href.includes('test.peekapak.com')) {
      targetDomain = 'test.peekapak.com';
    } else if (href.includes('localhost')) {
      targetDomain = 'localhost';
    }
  }

  const allowedBuilderDomains = ['localhost', 'test.peekapak.com'];

  // Only initialize Builder.io for allowed domains
  if (allowedBuilderDomains.includes(targetDomain)) {
    builder.init(import.meta.env.REACT_APP_PUBLIC_BUILDER_KEY);
    console.log('🔧 Builder.io initialized for:', targetDomain, isBuilderPreview ? '(in preview)' : '(direct)');
  } else {
    console.log('🚫 Builder.io disabled for:', targetDomain);
  }
}

async function main() {
  if (typeof JSON === 'undefined') {
    alert(`This website requires a modern browser such as: Chrome, Safari, Firefox,
        or Internet Explorer v11+`);
  }

  // Debug logging for Builder.io preview troubleshooting
  const isBuilderPreview = window.location.hostname.includes('preview.builder.codes');
  const isLocalhost = window.location.hostname === 'localhost';

  if (isBuilderPreview || isLocalhost) {
    console.group('🚀 Application Initialization Debug');
    console.log('🔍 Startup Environment:', {
      isBuilderPreview,
      isLocalhost,
      hostname: window.location.hostname,
      origin: window.location.origin,
      pathname: window.location.pathname,
      nodeEnv: import.meta.env.MODE,
      builderKey: import.meta.env.REACT_APP_PUBLIC_BUILDER_KEY ? 'Set' : 'Not set',
    });
    console.groupEnd();
  }

  // the following sequence of function calls is not
  // idempotent since there are side effects from the
  // initialization of initial application state
  //
  await initializeApplicationState(); // Now async to support data residency
  initializeApplicationRoot();
  initializeMixpanel();
  initializeBuilder();

  const tagManagerArgs = {
    gtmId: 'GTM-M7XZPGC',
    auth: 'K4RwN8ehr-OfaEm0BBaMRw',
    preview: 'env-1',
    events: {
      requestInfoSuccess: 'Request Info Sucesss',
    },
  };
  TagManager.initialize(tagManagerArgs);

  initializeErrorTracker();

  const queryString = parseQuery(window.location.search, window.location.hash);

  const store = getStore(); // render depends on this

  const maintenanceMode = await (async () => {
    try {
      const result = await getPlatformMode();
      return result;
    } catch (error) {
      return { mode: 'production' };
    }
  })();

  if (maintenanceMode.mode === 'maintenance') {
    renderMaintenancePage();
  } else if ('route' in queryString) {
    const newRoute = `${queryString.route}`;
    const newRouteWithQuery = buildRouteWithQuery(newRoute, queryString);

    myBrowserHistory.replace(`/${newRouteWithQuery}`);
    renderRouterConfiguration(Root);
  } else {
    renderRouterConfiguration(Root);
  }

  function renderMaintenancePage() {
    ReactDOM.render(
      <Provider store={store}>
        <HelmetProvider>
          <StrictMode>
            <SingleMessagePage
              graphic=<img style={{ width: '40em', marginBottom: '4em' }} src={logoImage} alt='Peekapak Logo' />
              title='Maintenance'
              body=<p>We are currently performing maintenance on our site. Please check back later.</p>
            />
          </StrictMode>
        </HelmetProvider>
      </Provider>,
      document.getElementById('root'),
    );
  }

  function renderRouterConfiguration(Component: React.FC) {
    ReactDOM.render(
      <Provider store={store}>
        <HelmetProvider>
          <StrictMode>
            <Component />
          </StrictMode>
        </HelmetProvider>
      </Provider>,
      document.getElementById('root'),
    );
  }

  function buildRouteWithQuery(rootRoute, queryStringObject) {
    const copy = queryStringObject;
    delete copy.route;

    // properly encode each element in the path of the route
    const elements = rootRoute.split('/');
    const encodedElements = [];
    elements.forEach((element) => {
      encodedElements.push(fixedEncodeURIComponent(element));
    });

    let newRoute = encodedElements.join('/');
    const keys = Object.keys(copy);
    let remaining = keys.length;

    if (remaining > 0) {
      const nextKey = keys.pop();
      const nextValue = fixedEncodeURIComponent(copy[nextKey]);
      newRoute = `${newRoute}?${nextKey}=${nextValue}`;
      remaining--;
    }

    while (remaining) {
      const nextKey = keys.pop();
      const nextValue = fixedEncodeURIComponent(copy[nextKey]);
      newRoute = `${newRoute}&${nextKey}=${nextValue}`;
      remaining--;
    }

    return newRoute;
  }
}

reportWebVitals();
